import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Approval {
    id: string;
    tenantId: string;
    title: string;
    description?: string;
    requesterId: string;
    approverId: string;
    entityType: string;
    entityId: string;
    status: string;
    comments?: string;
    createdAt: string;
    updatedAt: string;
}

interface ApprovalState {
    approvals: Approval[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchApprovals: (filters?: any) => Promise<void>;
    createApproval: (data: Partial<Approval>) => Promise<void>;
    updateApproval: (id: string, data: Partial<Approval>) => Promise<void>;
    deleteApproval: (id: string) => Promise<void>;
}

export const useApprovalStore = create<ApprovalState>((set, get) => ({
    approvals: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
    },
    fetchApprovals: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/work/approvals', { params: filters });
            const { data, pagination } = response.data;
            set({ approvals: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch approvals', isLoading: false });
        }
    },
    createApproval: async (data: Partial<Approval>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/work/approvals', data);
            const newApproval = response.data;
            set((state) => ({
                approvals: [newApproval, ...state.approvals].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create approval', isLoading: false });
            throw error;
        }
    },
    updateApproval: async (id: string, data: Partial<Approval>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/work/approvals/${id}`, data);
            const updatedApproval = response.data;
            set((state) => ({
                approvals: state.approvals.map((o) => (o.id === id ? updatedApproval : o)).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update approval', isLoading: false });
            throw error;
        }
    },
    deleteApproval: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/work/approvals/${id}`);
            set((state) => ({ approvals: state.approvals.filter((a) => a.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete approval' });
            throw error;
        }
    },
}));
